import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { SHOW_RESULTS } from "../data/content";

const LINKS = [
    { name: "How We Help", path: "/how-we-help" },
    { name: "GrowVise 90", path: "/growvise-90" },
    { name: "NZ Market Entry", path: "/market-entry-distribution" },
    ...(SHOW_RESULTS ? [{ name: "Our Results", path: "/our-results" }] : []),
    { name: "About", path: "/about" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                    scrolled ? "bg-navy-950/95 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between gap-6">
                    <Link to="/" data-testid="nav-home-link" aria-label="GrowVise home" className="shrink-0">
                        <Logo header />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-6 xl:gap-7" aria-label="Primary">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.path}
                                to={l.path}
                                data-testid={`nav-link-${l.path.slice(1)}`}
                                className={({ isActive }) =>
                                    `whitespace-nowrap text-[13.5px] font-medium transition-colors duration-200 ${
                                        isActive ? "text-gold-400" : "text-slate-300 hover:text-white"
                                    }`
                                }
                            >
                                {l.name}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-5 shrink-0">
                        <Link
                            to="/business-health-check"
                            data-testid="nav-healthcheck-btn"
                            className="whitespace-nowrap text-[13px] font-medium text-slate-300 hover:text-gold-400 transition-colors duration-200"
                        >
                            Health Check
                        </Link>
                        <Link
                            to="/book-a-conversation"
                            data-testid="nav-book-call-btn"
                            className="whitespace-nowrap bg-gold-500 text-navy-950 text-[13.5px] font-semibold px-5 py-2.5 rounded-md transition-colors duration-200 hover:bg-gold-400"
                        >
                            Book a Conversation
                        </Link>
                    </div>

                    <button
                        className="lg:hidden text-slate-200 p-2 -mr-2"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-btn"
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-navy-950 lg:hidden overflow-y-auto"
                        data-testid="mobile-menu"
                    >
                        <div className="pt-28 pb-12 px-6 flex flex-col">
                            {[{ name: "Home", path: "/" }, ...LINKS].map((l, i) => (
                                <motion.div
                                    key={l.path}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 + i * 0.05, duration: 0.4, ease: [0.25, 0.6, 0.3, 1] }}
                                >
                                    <NavLink
                                        to={l.path}
                                        data-testid={`mobile-nav-link-${l.path === "/" ? "home" : l.path.slice(1)}`}
                                        className={({ isActive }) =>
                                            `block font-serif text-[1.7rem] py-4 border-b border-white/10 transition-colors duration-200 ${
                                                isActive ? "text-gold-400" : "text-slate-100"
                                            }`
                                        }
                                    >
                                        {l.name}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4, duration: 0.4 }}
                                className="flex flex-col gap-3 mt-9"
                            >
                                <Link to="/book-a-conversation" data-testid="mobile-book-call-btn" className="btn-gold justify-center">
                                    Book a Conversation <ArrowRight size={16} />
                                </Link>
                                <Link to="/business-health-check" data-testid="mobile-healthcheck-btn" className="btn-outline justify-center">
                                    Free Business Health Check
                                </Link>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
